import { join } from "node:path";
import { tmpdir } from "node:os";
import { mkdtemp, rm, writeFile as writeFileMode } from "node:fs/promises";
import { exists, writeFile } from "./fsx.js";
import { run, which } from "./exec.js";

/**
 * Multi-target storefront deployment.
 *
 * The storefront is a Next.js app that ships with an OpenNext/Cloudflare config,
 * so Cloudflare is the native target and stays the default. Vercel runs Next.js
 * natively too. Netlify and Fly need a config file the template does not carry,
 * so they are supported by writing that config first — see `ensureConfig`.
 * Anything we cannot honestly deploy reports `ready: false` with the reason,
 * rather than shelling out and failing halfway.
 */

export type TargetId = "cloudflare" | "vercel" | "netlify" | "fly";

export const TARGET_IDS: readonly TargetId[] = ["cloudflare", "vercel", "netlify", "fly"] as const;

export function isTargetId(v: string): v is TargetId {
  return (TARGET_IDS as readonly string[]).includes(v);
}

export interface TargetStatus {
  id: TargetId;
  label: string;
  /** CLI resolvable on PATH, directly or via npx. */
  installed: boolean;
  /** CLI reports an authenticated session. */
  authenticated: boolean;
  /** Project carries this target's config file. */
  configured: boolean;
  /** Deployable right now. */
  ready: boolean;
  /** Why not ready, when it is not. */
  reason?: string;
  /** Account identity, when the CLI reports one. */
  account?: string;
}

export interface DeployOutcome {
  ok: boolean;
  target: TargetId;
  projectName: string;
  url?: string;
  reason?: string;
}

interface TargetDef {
  id: TargetId;
  label: string;
  bin: string;
  /** npx package when the binary is not installed globally. Empty = no npx path. */
  npxPkg: string;
  /** Config files that mark a project as set up for this target. */
  markers: string[];
  /** package.json script that produces this target's build artifact. */
  buildScript: string;
  /** Artifact required before deploying, relative to the storefront root. */
  artifact?: string;
  /** Argv that reports the logged-in identity. */
  whoami: string[];
  /** Command to run to log in, shown in guidance. */
  loginHint: string;
  /**
   * Read the account identity out of the CLI's output. Given stdout and stderr
   * separately, because several of these CLIs print a version banner to stderr
   * that would otherwise be mistaken for the answer.
   */
  parseAccount?: (stdout: string, stderr: string) => string | undefined;
  deployArgs: (projectName: string) => string[];
  /** Write config this target needs but the template does not ship. */
  ensureConfig?: (storefront: string, projectName: string) => Promise<void>;
  /** Argv that sets one secret. Value goes on stdin when `stdin` is set. */
  secretArgs: (key: string, value: string, projectName: string) => { args: string[]; stdin?: string };
}

const DEFS: Record<TargetId, TargetDef> = {
  cloudflare: {
    id: "cloudflare",
    label: "Cloudflare Workers",
    bin: "wrangler",
    npxPkg: "wrangler",
    markers: ["wrangler.jsonc", "wrangler.json", "wrangler.toml"],
    buildScript: "cf:build",
    artifact: join(".open-next", "worker.js"),
    whoami: ["whoami"],
    loginHint: "wrangler login",
    // wrangler writes this mid-sentence, so the trailing period is not part of it.
    parseAccount: (stdout, stderr) =>
      `${stdout}${stderr}`.match(/associated with the email\s+(\S+?)[.,]?(?:\s|$)/)?.[1],
    deployArgs: (name) => ["deploy", "--name", name],
    secretArgs: (key, value, name) => ({ args: ["secret", "put", key, "--name", name], stdin: value }),
  },
  vercel: {
    id: "vercel",
    label: "Vercel",
    bin: "vercel",
    npxPkg: "vercel",
    markers: ["vercel.json", ".vercel"],
    buildScript: "build",
    whoami: ["whoami"],
    loginHint: "vercel login",
    // The username is the only thing on stdout; the version banner goes to stderr.
    parseAccount: (stdout) =>
      stdout
        .trim()
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l && !l.startsWith(">") && !/^Vercel CLI/i.test(l))
        .pop(),
    deployArgs: () => ["deploy", "--prod", "--yes"],
    secretArgs: (key, value) => ({ args: ["env", "add", key, "production", "--force"], stdin: value }),
  },
  netlify: {
    id: "netlify",
    label: "Netlify",
    bin: "netlify",
    npxPkg: "netlify-cli",
    markers: ["netlify.toml"],
    buildScript: "build",
    whoami: ["status"],
    loginHint: "netlify login",
    parseAccount: (stdout, stderr) => `${stdout}${stderr}`.match(/Email:\s*(\S+)/)?.[1],
    deployArgs: () => ["deploy", "--build", "--prod"],
    ensureConfig: async (storefront) => {
      const path = join(storefront, "netlify.toml");
      if (await exists(path)) return;
      // Next.js on Netlify is only supported through the official plugin.
      await writeFile(
        path,
        [
          "# Written by @carrierllc/mcp",
          "[build]",
          '  command = "npm run build"',
          '  publish = ".next"',
          "",
          "[[plugins]]",
          '  package = "@netlify/plugin-nextjs"',
          "",
        ].join("\n"),
      );
    },
    secretArgs: (key, value) => ({ args: ["env:set", key, value] }),
  },
  fly: {
    id: "fly",
    label: "Fly.io",
    bin: "flyctl",
    npxPkg: "",
    markers: ["fly.toml"],
    buildScript: "build",
    whoami: ["auth", "whoami"],
    loginHint: "flyctl auth login",
    parseAccount: (out) => out.trim().split("\n").pop()?.trim(),
    deployArgs: () => ["deploy", "--now"],
    ensureConfig: async (storefront, projectName) => {
      const toml = join(storefront, "fly.toml");
      if (!(await exists(toml))) {
        await writeFile(
          toml,
          [
            "# Written by @carrierllc/mcp",
            `app = "${projectName}"`,
            "",
            "[build]",
            '  dockerfile = "Dockerfile"',
            "",
            "[http_service]",
            "  internal_port = 3000",
            "  force_https = true",
            "  auto_stop_machines = true",
            "  auto_start_machines = true",
            "",
          ].join("\n"),
        );
      }
      const dockerfile = join(storefront, "Dockerfile");
      if (!(await exists(dockerfile))) {
        // Fly runs a container, so the Next server needs one — the template has none.
        await writeFile(
          dockerfile,
          [
            "# Written by @carrierllc/mcp",
            "FROM node:22-slim AS build",
            "WORKDIR /app",
            "COPY package*.json ./",
            "RUN npm install",
            "COPY . .",
            "RUN npm run build",
            "",
            "FROM node:22-slim",
            "WORKDIR /app",
            "ENV NODE_ENV=production PORT=3000",
            "COPY --from=build /app ./",
            "EXPOSE 3000",
            'CMD ["npm", "run", "start"]',
            "",
          ].join("\n"),
        );
      }
    },
    secretArgs: (key, value) => ({ args: ["secrets", "set", `${key}=${value}`] }),
  },
};

export function targetLabel(id: TargetId): string {
  return DEFS[id].label;
}

/**
 * The name this project already deploys under, if it declares one.
 *
 * Cloudflare identifies a Worker by name, so deriving the name from the brand
 * instead of the project config does not rename a deployment — it creates a
 * second one and abandons the first, along with its routes, custom domain and
 * secrets. Observed: a storefront configured as `storefront` redeployed as
 * `bananas`, leaving the live site untouched and broken.
 *
 * So the config wins whenever it has a name, exactly as a configured host wins
 * in `rankTargets`.
 */
export async function configuredProjectName(
  id: TargetId,
  storefront: string,
): Promise<string | undefined> {
  if (id !== "cloudflare") return undefined;
  for (const marker of DEFS.cloudflare.markers) {
    const path = join(storefront, marker);
    if (!(await exists(path))) continue;
    const body = await readFileText(path);
    // JSONC — a comment-tolerant match beats a parse that would choke on them.
    const name = body.match(/^\s*"?name"?\s*:\s*"([^"]+)"/m)?.[1];
    if (name) return name;
  }
  return undefined;
}

/**
 * Route a custom domain to the Worker.
 *
 * `custom_domain: true` makes Cloudflare provision the DNS record and the
 * certificate itself, which is the only reason this is worth automating. It has
 * to live in wrangler config — there is no CLI flag for it.
 *
 * Requires the domain's zone to be on the same Cloudflare account. We do not
 * check that here; a deploy against a zone the account does not hold fails with
 * Cloudflare's own error, which says more than a guess would.
 */
export async function setCustomDomain(storefront: string, domain: string): Promise<boolean> {
  const clean = domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
  if (!clean || !clean.includes(".")) return false;

  const path = join(storefront, "wrangler.jsonc");
  if (!(await exists(path))) return false;
  const body = await readFileText(path);
  if (body.includes(`"pattern": "${clean}"`)) return true; // already routed

  // JSONC, so this is a targeted textual insert rather than a parse/serialise
  // round-trip that would strip the file's comments.
  const routes = `  "routes": [\n    { "pattern": "${clean}", "custom_domain": true }\n  ],\n`;
  const anchor = body.indexOf(`"main"`);
  if (anchor === -1) return false;
  const lineStart = body.lastIndexOf("\n", anchor) + 1;
  const patched = body.slice(0, lineStart) + routes + body.slice(lineStart);
  await writeFile(path, patched);
  return true;
}

async function readFileText(path: string): Promise<string> {
  const { readFile } = await import("./fsx.js");
  return readFile(path, "utf8");
}

/**
 * Revert to the previously deployed version.
 *
 * Only Cloudflare is wired: it is the one host here with a single-command
 * instant rollback. Everywhere else this reports false so the caller says
 * "left as-is" rather than implying a revert that never happened.
 */
export async function rollback(
  id: TargetId,
  storefront: string,
  projectName: string,
): Promise<{ ok: boolean; reason?: string }> {
  if (id !== "cloudflare") {
    return { ok: false, reason: `${DEFS[id].label} has no one-command rollback — revert manually.` };
  }
  const def = DEFS[id];
  const resolved = await resolveBin(def);
  if (!resolved) return { ok: false, reason: "wrangler not found." };
  const r = await run(
    resolved.bin,
    [...resolved.prefix, "rollback", "--name", projectName, "--yes"],
    { cwd: storefront, timeoutMs: 300_000 },
  );
  return r.ok
    ? { ok: true }
    : { ok: false, reason: `${r.stderr || r.stdout}`.trim().split("\n").slice(-2).join(" ").slice(0, 300) };
}

/** Exposed so the account parsers can be tested against real CLI output. */
export function parseAccountForTest(
  id: TargetId,
  stdout: string,
  stderr: string,
): string | undefined {
  return DEFS[id].parseAccount?.(stdout, stderr);
}

export function loginHint(id: TargetId): string {
  return DEFS[id].loginHint;
}

/** How to invoke a target's CLI: direct binary, npx, or not at all. */
async function resolveBin(def: TargetDef): Promise<{ bin: string; prefix: string[] } | undefined> {
  if (await which(def.bin)) return { bin: def.bin, prefix: [] };
  if (def.npxPkg && (await which("npx"))) return { bin: "npx", prefix: [def.npxPkg] };
  return undefined;
}

/** Probe one target: installed, authenticated, and configured for this project. */
export async function probeTarget(id: TargetId, storefront: string): Promise<TargetStatus> {
  const def = DEFS[id];
  let configured = false;
  for (const marker of def.markers) {
    if (await exists(join(storefront, marker))) {
      configured = true;
      break;
    }
  }

  const resolved = await resolveBin(def);
  if (!resolved) {
    return {
      id,
      label: def.label,
      installed: false,
      authenticated: false,
      configured,
      ready: false,
      reason: def.npxPkg
        ? `${def.bin} not found — install it, or make npx available.`
        : `${def.bin} not found — install the Fly CLI (brew install flyctl).`,
    };
  }

  const who = await run(resolved.bin, [...resolved.prefix, ...def.whoami], {
    cwd: storefront,
    timeoutMs: 60_000,
  });
  if (!who.ok) {
    return {
      id,
      label: def.label,
      installed: true,
      authenticated: false,
      configured,
      ready: false,
      reason: `${def.bin} is not logged in — run \`${def.loginHint}\`.`,
    };
  }

  return {
    id,
    label: def.label,
    installed: true,
    authenticated: true,
    configured,
    ready: true,
    account: def.parseAccount?.(who.stdout, who.stderr),
  };
}

/** Probe every target. Order is stable; ranking is a separate concern. */
export async function probeAll(storefront: string): Promise<TargetStatus[]> {
  return Promise.all(TARGET_IDS.map((id) => probeTarget(id, storefront)));
}

/**
 * Rank the deployable targets.
 *
 * A target the project is already configured for wins, because that config was a
 * deliberate choice. Otherwise fall back to declaration order, which puts
 * Cloudflare first — the one the template is actually built for.
 */
export function rankTargets(statuses: TargetStatus[]): TargetStatus[] {
  return statuses
    .filter((s) => s.ready)
    .sort((a, b) => {
      if (a.configured !== b.configured) return a.configured ? -1 : 1;
      return TARGET_IDS.indexOf(a.id) - TARGET_IDS.indexOf(b.id);
    });
}

/** The build script this target needs (`cf:build` for Cloudflare, else `build`). */
export function buildScriptFor(id: TargetId): string {
  return DEFS[id].buildScript;
}

/** True when the target needs a build artifact that is not on disk yet. */
export async function artifactMissing(id: TargetId, storefront: string): Promise<boolean> {
  const artifact = DEFS[id].artifact;
  if (!artifact) return false;
  return !(await exists(join(storefront, artifact)));
}

/** Best-effort deployed URL, read back out of the target CLI's own output. */
export function parseDeployedUrl(output: string): string | undefined {
  return output.match(
    /https:\/\/[^\s"']+\.(?:workers\.dev|vercel\.app|netlify\.app|fly\.dev)[^\s"']*/,
  )?.[0];
}

/** Push one secret to a target. Never logs the value. */
export async function putSecret(
  id: TargetId,
  storefront: string,
  projectName: string,
  key: string,
  value: string,
): Promise<boolean> {
  const def = DEFS[id];
  const resolved = await resolveBin(def);
  if (!resolved) return false;
  const { args, stdin } = def.secretArgs(key, value, projectName);
  const r = await run(resolved.bin, [...resolved.prefix, ...args], {
    cwd: storefront,
    timeoutMs: 120_000,
    stdin,
  });
  return r.ok;
}

export interface StagedSecrets {
  /** Keys that are now guaranteed to be live when the deploy lands. */
  staged: string[];
  failed: string[];
  /** Passed to `deployTo` so Cloudflare can ship them in the same version. */
  secretsFile?: string;
  /** Called after the deploy, success or not. */
  cleanup: () => Promise<void>;
}

/**
 * Put secrets in place *before or with* the code, never after.
 *
 * Pushing secrets after a successful deploy leaves a window where the new code
 * is serving without them. That window is not theoretical: it is why a
 * storefront deployed green and then returned 500 on every catalog page.
 *
 * Each host has its own way of closing it:
 *   Cloudflare — `--secrets-file` on `wrangler deploy`, one atomic version.
 *   Fly        — `secrets set --stage`, applied by the deploy that follows.
 *   Vercel     — `env add`, picked up by the next deployment.
 *   Netlify    — `env:set`, picked up by the next build.
 */
export async function stageSecrets(
  id: TargetId,
  storefront: string,
  projectName: string,
  secrets: Record<string, string>,
): Promise<StagedSecrets> {
  const entries = Object.entries(secrets).filter(([, v]) => v?.trim());
  const noop: StagedSecrets = { staged: [], failed: [], cleanup: async () => {} };
  if (entries.length === 0) return noop;

  const def = DEFS[id];
  const resolved = await resolveBin(def);
  if (!resolved) return { staged: [], failed: entries.map(([k]) => k), cleanup: async () => {} };

  if (id === "cloudflare") {
    // A file rather than N `secret put` calls: one API call, one version, and no
    // interval where the Worker is live without them.
    const dir = await mkdtemp(join(tmpdir(), "carrier-secrets-"));
    const file = join(dir, ".env");
    const body = entries.map(([k, v]) => `${k}=${v}`).join("\n");
    // 0600: this is the one place secret values touch disk.
    await writeFileMode(file, `${body}\n`, { mode: 0o600 });
    return {
      staged: entries.map(([k]) => k),
      failed: [],
      secretsFile: file,
      cleanup: async () => {
        await rm(dir, { recursive: true, force: true });
      },
    };
  }

  if (id === "fly") {
    // --stage defers the machine update to the deploy, avoiding a second restart.
    const args = ["secrets", "set", "--stage", ...entries.map(([k, v]) => `${k}=${v}`)];
    const r = await run(resolved.bin, [...resolved.prefix, ...args], {
      cwd: storefront,
      timeoutMs: 180_000,
    });
    return {
      staged: r.ok ? entries.map(([k]) => k) : [],
      failed: r.ok ? [] : entries.map(([k]) => k),
      cleanup: async () => {},
    };
  }

  // Vercel and Netlify: set them first, the following deploy picks them up.
  const staged: string[] = [];
  const failed: string[] = [];
  for (const [key, value] of entries) {
    const ok = await putSecret(id, storefront, projectName, key, value);
    (ok ? staged : failed).push(key);
  }
  return { staged, failed, cleanup: async () => {} };
}

/** Deploy the storefront to one target. Assumes the build already ran. */
export async function deployTo(
  id: TargetId,
  storefront: string,
  projectName: string,
  opts: { secretsFile?: string } = {},
): Promise<DeployOutcome> {
  const def = DEFS[id];
  const resolved = await resolveBin(def);
  if (!resolved) {
    return { ok: false, target: id, projectName, reason: `${def.bin} not found.` };
  }
  await def.ensureConfig?.(storefront, projectName);

  if (await artifactMissing(id, storefront)) {
    return {
      ok: false,
      target: id,
      projectName,
      reason: `No ${def.artifact} build found — run the build step first.`,
    };
  }

  const args = [...resolved.prefix, ...def.deployArgs(projectName)];
  // Cloudflare can carry the secrets in the same version as the code.
  if (id === "cloudflare" && opts.secretsFile) args.push("--secrets-file", opts.secretsFile);

  // Captured rather than inherited, so the deployed URL can be parsed back out.
  const r = await run(resolved.bin, args, {
    cwd: storefront,
    timeoutMs: 900_000,
  });
  const combined = `${r.stdout}${r.stderr}`;
  if (!r.ok) {
    const tail = combined.trim().split("\n").slice(-3).join(" ").slice(0, 400);
    return { ok: false, target: id, projectName, reason: tail || `${def.bin} deploy failed.` };
  }
  return { ok: true, target: id, projectName, url: parseDeployedUrl(combined) };
}
